import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { cn } from 'Utils/cn';
import { cva } from 'class-variance-authority';
import Spinner from 'Components/Atoms/Spinner';

export const buttonVariants = cva(
    'inline-flex items-center justify-center whitespace-nowrap rounded-md focus-visible:outline-none focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-75 ring-primary/20 focus-visible:border-primary transition-all relative text-sm font-medium no-underline aria-busy:pointer-events-none',
    {
        variants: {
            variant: {
                default:
                    'bg-white border border-gray-200 shadow-sm hover:bg-gray-50 text-dark-blue disabled:shadow-none disabled:bg-gray-100 disabled:text-gray-400',
                primary: 'bg-dark-blue text-white shadow-sm hover:bg-dark-blue/80 disabled:shadow-none',
                ghost: 'bg-transparent text-dark-blue/60 hover:text-dark-blue',
                tertiary: 'bg-warning text-white hover:bg-warning/80',
                warning: 'bg-warning text-white shadow-sm hover:bg-warning/80 disabled:shadow-none',
            },
            size: {
                default: 'h-11 px-6',
                small: 'h-9 px-4 text-xs',
                extraSmall: 'h-8 px-4 text-xs',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

const BaseButton = forwardRef(function BaseButton(props, ref) {
    const { className, variant, size, isBusy, disabled, type = 'button', href, target, children, ...rest } = props;
    const isDisabled = isBusy || disabled;

    if (href) {
        const defaultRel = target === '_blank' ? 'noopener' : undefined;
        return (
            <a
                href={href}
                target={target}
                rel={defaultRel}
                aria-busy={isBusy}
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...rest}
            >
                {children}
            </a>
        );
    }

    return (
        <button
            type={type}
            disabled={isDisabled}
            className={cn(buttonVariants({ variant, size, className }))}
            ref={ref}
            {...rest}
        >
            {children}
        </button>
    );
});

BaseButton.propTypes = {
    className: PropTypes.string,
    variant: PropTypes.oneOf(['default', 'primary', 'ghost', 'tertiary', 'warning']),
    size: PropTypes.oneOf(['default', 'small', 'extraSmall']),
    isBusy: PropTypes.bool,
    disabled: PropTypes.bool,
    children: PropTypes.node,
    type: PropTypes.oneOf(['button', 'submit']),
    href: PropTypes.string,
    target: PropTypes.oneOf(['_blank', '_self', '_parent', '_top']),
    testId: PropTypes.string,
};

const Button = forwardRef(function Button(props, ref) {
    const { children, isBusy, testId, ...rest } = props;

    return (
        <BaseButton ref={ref} isBusy={isBusy} data-testid={testId} {...rest}>
            {isBusy && (
                <span className="absolute inset-0 flex items-center justify-center">
                    <Spinner size={24} />
                </span>
            )}

            <span
                className={cn(
                    'inline-flex items-center gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:first:-ml-1',
                    {
                        'opacity-0': isBusy,
                    },
                )}
            >
                {children}
            </span>
        </BaseButton>
    );
});

export default Button;

Button.propTypes = {
    className: PropTypes.string,
    variant: PropTypes.oneOf(['default', 'primary', 'ghost', 'tertiary', 'warning']),
    size: PropTypes.oneOf(['default', 'small', 'extraSmall']),
    isBusy: PropTypes.bool,
    disabled: PropTypes.bool,
    children: PropTypes.node,
    type: PropTypes.oneOf(['button', 'submit']),
    href: PropTypes.string,
    target: PropTypes.oneOf(['_blank', '_self', '_parent', '_top']),
    testId: PropTypes.string,
};
